import { useMemo } from "react";
import { useCreateContractWizardStore } from "@/stores/createContractWizardStore";
import { useCreateOrderFromContractStore } from "@/stores/createOrderFromContractStore";
import { useTradeLock } from "@/hooks/useTradeLock";
import { getContractParties } from "@/utils/Contracts";

/**
 * Drop-in replacements for the wizard stores' `open` actions that respect the company trade lock.
 *
 * Always prefer these over reading `open` off the store directly, so a new entry point cannot silently skip
 * the lock check. Memoised because callers pass the result into `useMemo` dependency arrays (see the table
 * column defs in `pages/Contracts/ContractList`), where a fresh identity each render would defeat the memo.
 *
 * A new contract's counterparty is only chosen inside the wizard, so `useOpenCreateContract` can check our own
 * lock alone — the partner picker takes care of the rest. An order is always placed on a contract we already
 * hold, so `useOpenCreateOrder` also refuses when the contract's counterparty is locked.
 */
export function useOpenCreateContract() {
  const open = useCreateContractWizardStore((s) => s.open);
  const { guardTradeLock } = useTradeLock();
  return useMemo(() => guardTradeLock(open), [guardTradeLock, open]);
}

export function useOpenCreateOrder() {
  const open = useCreateOrderFromContractStore((s) => s.open);
  const { guardTradeLock } = useTradeLock();
  return useMemo(() => guardTradeLock(open, getContractParties), [guardTradeLock, open]);
}
